import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  Add,
  Delete,
  ContentCopy,
  Folder,
  Key,
  Person,
  CheckCircle,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

/**
 * SFTP Tab Component with list view and copyable credentials
 */
const SiteSftpTab = ({
  site,
  siteId,
  sftpUsers,
  onCreateUser,
  onDeleteUser,
  loading,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    siteFolder: site?.name || "",
  });
  const [formErrors, setFormErrors] = useState({});

  const handleCopy = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const CopyButton = ({ text, field }) => (
    <IconButton size="small" onClick={() => handleCopy(text, field)}>
      {copiedField === field ? (
        <CheckCircle fontSize="small" color="success" />
      ) : (
        <ContentCopy fontSize="small" />
      )}
    </IconButton>
  );

  const validateForm = () => {
    const errors = {};
    if (!formData.username) {
      errors.username = "Username is required";
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      errors.username =
        "Username can only contain letters, numbers, underscores, and hyphens";
    }
    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    }
    if (!formData.siteFolder) {
      errors.siteFolder = "Site folder is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenDialog = () => {
    setFormData({
      username: "",
      password: "",
      siteFolder: site?.name || "",
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setShowPassword(false);
    setFormData({ username: "", password: "", siteFolder: site?.name || "" });
    setFormErrors({});
  };

  const handleCreateUser = async () => {
    if (!validateForm()) return;
    try {
      await onCreateUser({
        username: formData.username,
        password: formData.password,
        siteFolder: formData.siteFolder,
        siteId,
      });
      handleCloseDialog();
    } catch (error) {
      setFormErrors({ submit: error.message });
    }
  };

  const handleOpenDeleteConfirm = (user) => {
    setSelectedUser(user);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setSelectedUser(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedUser) {
      await onDeleteUser({ siteId, username: selectedUser.username });
      handleCloseDeleteConfirm();
    }
  };

  const generatePassword = () => {
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@%^*_-+.~";
    const array = new Uint32Array(16);
    crypto.getRandomValues(array);
    let password = "";
    for (let i = 0; i < 16; i++) {
      password += charset.charAt(array[i] % charset.length);
    }
    setFormData({ ...formData, password });
  };

  const users = sftpUsers?.users || [];
  const sftpHost =
    users[0]?.connection?.host && users[0].connection.host !== "localhost"
      ? users[0].connection.host
      : site?.worker?.ipAddress ||
        process.env.REACT_APP_SFTP_HOST ||
        "Set SFTP_HOST in env";
  const sftpPort = users[0]?.connection?.port || 2222;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            SFTP Access
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage SFTP users for <strong>{site?.name || "this site"}</strong>
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          onClick={handleOpenDialog}
          disabled={loading}
        >
          Create User
        </Button>
      </Box>

      {/* Connection Info Bar */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Connection:</strong> Host: <strong>{sftpHost}</strong> | Port:{" "}
          <strong>{sftpPort}</strong> | Protocol: <strong>SFTP</strong> — After
          connecting, your WordPress files are in the <strong>/html</strong>{" "}
          directory.
        </Typography>
      </Alert>

      {/* Users Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            bgcolor: "action.hover",
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Folder sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No SFTP Users
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create an SFTP user to access your WordPress files via FileZilla,
            WinSCP, or any SFTP client.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Add />}
            onClick={handleOpenDialog}
          >
            Create First User
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Host</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Port</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Directory</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.username} hover>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ fontFamily: "monospace" }}
                      >
                        {user.username}
                      </Typography>
                      <CopyButton
                        text={user.username}
                        field={`user-${user.username}`}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {user.connection?.host || sftpHost}
                      </Typography>
                      <CopyButton
                        text={user.connection?.host || sftpHost}
                        field={`host-${user.username}`}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {user.connection?.port || sftpPort}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        /html
                      </Typography>
                      <CopyButton text="/html" field={`dir-${user.username}`} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label="Active" size="small" color="success" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete User">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleOpenDeleteConfirm(user)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create User Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create SFTP User</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              error={!!formErrors.username}
              helperText={
                formErrors.username ||
                "Only letters, numbers, underscores, and hyphens"
              }
              InputProps={{
                startAdornment: (
                  <Person sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
            />
            <Box>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                error={!!formErrors.password}
                helperText={
                  formErrors.password || "Minimum 8 characters required"
                }
                InputProps={{
                  startAdornment: (
                    <Key sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                size="small"
                onClick={generatePassword}
                sx={{ mt: 1 }}
                variant="outlined"
              >
                Generate Strong Password
              </Button>
            </Box>
            <TextField
              fullWidth
              label="Site Folder"
              value={formData.siteFolder}
              onChange={(e) =>
                setFormData({ ...formData, siteFolder: e.target.value })
              }
              error={!!formErrors.siteFolder}
              helperText={
                formErrors.siteFolder || "Reference name for this SFTP access"
              }
              InputProps={{
                startAdornment: (
                  <Folder sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
            />
            {formErrors.submit && (
              <Alert severity="error">{formErrors.submit}</Alert>
            )}
            <Alert severity="warning">
              <strong>Save these credentials!</strong> The password will only be
              shown once during creation. Copy and store it securely.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        maxWidth="sm"
      >
        <DialogTitle>Delete SFTP User</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to delete SFTP user{" "}
            <strong>{selectedUser?.username}</strong>? This will immediately
            revoke all SFTP access.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Delete User"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SiteSftpTab;
